import React, { useState } from 'react';
import { Lock, Zap } from 'lucide-react';
import PricingModal from './PricingModal';
import AuthModal from './AuthModal';

// ── PremiumBlur ───────────────────────────────────────────────────────────────
// Wraps content that is visible but blurred for free users.

interface PremiumBlurProps {
  children?: React.ReactNode;
  label?: string;
  highlight?: 'keywords' | 'scans' | 'export' | 'tlds';
  rows?: number; // how many blurred placeholder rows to show instead of real children
}

export const PremiumBlur: React.FC<PremiumBlurProps> = ({ children, label = 'Unlock with Premium', highlight, rows }) => {
  const [showPricing, setShowPricing] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  return (
    <>
      <div className="relative">
        {rows ? (
          <div className="space-y-0 pointer-events-none select-none" aria-hidden>
            {Array.from({ length: rows }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 blur-[3px]">
                <div className="w-6 text-sm text-gray-300">{i + 11}.</div>
                <div className="flex-1 h-4 bg-gray-200 rounded" style={{ width: `${55 + (i % 5) * 8}%` }} />
                <div className="w-16 h-4 bg-gray-200 rounded" />
                <div className="w-24 h-4 bg-gray-200 rounded hidden sm:block" />
              </div>
            ))}
          </div>
        ) : (
          <div className="blur-[3px] pointer-events-none select-none" aria-hidden>
            {children}
          </div>
        )}

        {/* Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[1px]">
          <div className="text-center px-6 py-4 bg-white border border-gray-200 rounded-xl shadow-sm max-w-xs mx-auto">
            <Lock className="h-5 w-5 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
            <p className="text-xs text-gray-500 mb-3">Get all keywords, unlimited scans and more.</p>
            <button
              onClick={() => setShowPricing(true)}
              className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <Zap className="h-3.5 w-3.5" />
              Go Premium
            </button>
          </div>
        </div>
      </div>

      {showPricing && (
        <PricingModal
          onClose={() => setShowPricing(false)}
          onSignIn={() => { setShowPricing(false); setShowAuth(true); }}
          highlight={highlight}
        />
      )}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  );
};

// ── PremiumTooltip ────────────────────────────────────────────────────────────
// Wraps a button/element that is visible but disabled for free users.

interface PremiumTooltipProps {
  children: React.ReactElement;
  label?: string;
  highlight?: 'keywords' | 'scans' | 'export' | 'tlds';
}

export const PremiumTooltip: React.FC<PremiumTooltipProps> = ({ children, label = 'Available on Premium', highlight }) => {
  const [showPricing, setShowPricing] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  return (
    <>
      <div className="relative inline-block">
        <div
          className="cursor-not-allowed"
          onClick={() => setShowPricing(true)}
          onMouseEnter={() => setTooltipVisible(true)}
          onMouseLeave={() => setTooltipVisible(false)}
        >
          <div className="pointer-events-none opacity-50">
            {children}
          </div>
        </div>
        {tooltipVisible && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 whitespace-nowrap">
            <div className="bg-gray-900 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg flex items-center gap-1.5">
              <Lock className="h-3 w-3 flex-shrink-0" />
              {label}
            </div>
          </div>
        )}
      </div>

      {showPricing && (
        <PricingModal
          onClose={() => setShowPricing(false)}
          onSignIn={() => { setShowPricing(false); setShowAuth(true); }}
          highlight={highlight}
        />
      )}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  );
};

// ── ScanLimitBanner ───────────────────────────────────────────────────────────

interface ScanLimitBannerProps {
  remaining: number; // 0 = limit reached
  onUpgrade: () => void;
}

export const ScanLimitBanner: React.FC<ScanLimitBannerProps> = ({ remaining, onUpgrade }) => {
  if (remaining > 1) return null;

  return (
    <div className={`px-4 py-2.5 text-sm flex items-center gap-3 ${remaining === 0 ? 'bg-red-50 border-b border-red-200' : 'bg-amber-50 border-b border-amber-200'}`}>
      <Zap className={`h-4 w-4 flex-shrink-0 ${remaining === 0 ? 'text-red-500' : 'text-amber-500'}`} />
      <span className={remaining === 0 ? 'text-red-800' : 'text-amber-800'}>
        {remaining === 0
          ? 'Daily scan limit reached (3/3).'
          : `${remaining} free scan remaining today.`}
      </span>
      <button onClick={onUpgrade} className={`ml-auto text-xs font-semibold underline underline-offset-2 whitespace-nowrap ${remaining === 0 ? 'text-red-700 hover:text-red-900' : 'text-amber-700 hover:text-amber-900'}`}>
        Upgrade for unlimited →
      </button>
    </div>
  );
};
